import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import LoginPage from "@/pages/auth/LoginPage";
import AppLayout from "@/components/layout/AppLayout";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import WarehousesPage from "@/pages/master-data/WarehousesPage";
import VehiclesPage from "@/pages/master-data/VehiclesPage";
import CitiesPage from "@/pages/master-data/CitiesPage";
import RegionsPage from "@/pages/master-data/RegionsPage";
import LeadSourcesPage from "@/pages/master-data/LeadSourcesPage";
import DeliveryAssociatesPage from "@/pages/master-data/DeliveryAssociatesPage";
import ComingSoon from "@/components/ComingSoon";
import WarehouseReportPage from "@/pages/reporting/WarehouseReportPage";
import VehicleReportPage from "@/pages/reporting/VehicleReportPage";
import TransferReportPage from "@/pages/reporting/TransferReportPage";
import DailySummaryPage from "@/pages/reporting/DailySummaryPage";
import LeadsPage from "@/pages/leads/LeadsPage";
import LeadDetailPage from "@/pages/leads/LeadDetailPage";
import UsersPage from "@/pages/users/UsersPage";
import BookingsPage from "@/pages/bookings/BookingsPage";
import BookingDetailPage from "@/pages/bookings/BookingDetailPage";
import TransfersPage from "@/pages/transfers/TransfersPage";
import TransferDetailPage from "@/pages/transfers/TransferDetailPage";
import ConsignmentsPage from "@/pages/consignments/ConsignmentsPage";
import ConsignmentDetailPage from "@/pages/consignments/ConsignmentDetailPage";
import CalendarPage from "@/pages/calendar/CalendarPage";
import FollowUpCalendarPage from "@/pages/calendar/FollowUpCalendarPage";
import MovementCalendarPage from "@/pages/calendar/MovementCalendarPage";
import SettingsPage from "@/pages/settings/SettingsPage";

import { Toaster } from "sonner";
import "@/App.css";

/* ── Loading screen ── */
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-4">
      <svg className="animate-spin h-8 w-8 text-[#1B2A6B]" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
      </svg>
      <p className="text-xs text-[#475569] tracking-widest uppercase font-semibold">Loading...</p>
    </div>
  </div>
);

/* ── Root redirect ── */
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
};

const RESTRICTED_ROLES = new Set(["supervisor", "relationship_manager", "warehouse_manager"]);
const ROLE_HOME = {
  supervisor: "/leads",
  relationship_manager: "/leads",
  warehouse_manager:        "/confirmed-leads",
};
const ROLE_ALLOWED = {
  supervisor: ["/dashboard", "/calendar", "/leads", "/confirmed-leads", "/consignments", "/trips", "/reporting", "/daily-summary"],
  relationship_manager: ["/dashboard", "/calendar", "/leads", "/confirmed-leads", "/consignments", "/trips"],
  warehouse_manager:        ["/dashboard", "/calendar/movement", "/confirmed-leads", "/consignments", "/trips"],
};
function homeFor(role) { return ROLE_HOME[role] || "/dashboard"; }
function canAccess(role, path) {
  if (!RESTRICTED_ROLES.has(role)) return true; // admin / any unrestricted role
  const allowed = ROLE_ALLOWED[role];
  if (!allowed) return true;
  return allowed.some(prefix => path === prefix || path.startsWith(prefix + "/"));
}

/* ── Protected layout (auth gate + sidebar shell) ── */
const ProtectedLayout = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccess(user.role, location.pathname)) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return <AppLayout />;
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected — all share the AppLayout shell */}
            <Route element={<ProtectedLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/master-data/warehouses" element={<WarehousesPage />} />
              <Route path="/master-data/vehicles" element={<VehiclesPage />} />
              <Route path="/master-data/cities" element={<CitiesPage />} />
              <Route path="/master-data/regions" element={<RegionsPage />} />
              <Route path="/master-data/lead-sources" element={<LeadSourcesPage />} />
              <Route path="/master-data/delivery-associates" element={<DeliveryAssociatesPage />} />
              <Route path="/reporting/warehouse-report" element={<WarehouseReportPage />} />
              <Route path="/reporting/transfer-report"  element={<TransferReportPage />} />
              <Route path="/reporting/vehicle-report"   element={<VehicleReportPage />} />
              <Route path="/daily-summary" element={<DailySummaryPage />} />
              <Route path="/leads" element={<Navigate to="/leads/all" replace />} />
              <Route path="/leads/all" element={<LeadsPage />} />
              <Route path="/leads/active" element={<LeadsPage />} />
              <Route path="/leads/confirmed" element={<LeadsPage />} />
              <Route path="/leads/closed" element={<LeadsPage />} />
              <Route path="/leads/:id" element={<LeadDetailPage />} />
              <Route path="/confirmed-leads" element={<Navigate to="/confirmed-leads/active" replace />} />
              <Route path="/confirmed-leads/active" element={<BookingsPage />} />
              <Route path="/confirmed-leads/completed" element={<BookingsPage />} />
              <Route path="/confirmed-leads/:id" element={<BookingDetailPage />} />
              <Route path="/consignments"    element={<Navigate to="/consignments/active" replace />} />
              <Route path="/consignments/active" element={<TransfersPage />} />
              <Route path="/consignments/completed" element={<TransfersPage />} />
              <Route path="/consignments/:id" element={<TransferDetailPage />} />
              <Route path="/trips" element={<Navigate to="/trips/active" replace />} />
              <Route path="/trips/active" element={<ConsignmentsPage />} />
              <Route path="/trips/completed" element={<ConsignmentsPage />} />
              <Route path="/trips/:id" element={<ConsignmentDetailPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/calendar/followup" element={<FollowUpCalendarPage />} />
              <Route path="/calendar/movement" element={<MovementCalendarPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>

            {/* Fallback */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
